/**
 * render.ts — HTML5 canvas renderer.
 *
 * Five layers, drawn back-to-front each frame (§8):
 *   1. Lake background    — pre-rendered gradient on an offscreen canvas.
 *   2. Heat-map overlay   — read directly from the bandit's 2-D spatial block.
 *   3. Fish flock         — silhouettes bobbing around the drifting center.
 *   4. True marker        — dotted ring at the actual flock centre.
 *   5. Hook + line + worm — eased travel; the bait is a wiggling worm.
 *
 * The renderer is imperative: a rAF loop + direct canvas writes is the
 * simplest thing that performs. State lives in `Lake` (the world) and
 * `HookState` (the animation); the renderer reads, never mutates.
 */

import { type Lake } from './environment';
import { type LinUcb } from './bandit';
import {
  defaultLayout,
  featurizePixel,
  topMarginalValue,
  topSpatial,
  R_BLOCK,
  G_BLOCK,
  B_BLOCK,
  SIZE_BLOCK,
  type AnchorLayout,
  type Action,
} from './featurize';

/** Heat-map render resolution. */
const HEATMAP_GRID = 72;

const LAKE_TOP = '#aee0f5';
const LAKE_BOTTOM = '#1d4e6b';
const HOOK_LINE = 'rgba(40, 30, 20, 0.7)';

/**
 * Bait worm segment table, in hook-local coordinates. The body is a chain of
 * overlapping circles that *wraps around* the hook shank:
 *   - `mob`   (mobility): 0 = pinned to the hook, 1 = free to swing. Drives
 *     the procedural wiggle so the impaled mid-body barely moves while the
 *     tail and head sway.
 *   - `layer`: 0 = drawn behind the hook, 1 = drawn in front. Interleaving
 *     the two is what sells the "threaded on the hook" look.
 *
 * Ported from the standalone worm-on-hook asset viewer.
 */
interface WormSegment {
  x: number;
  y: number;
  r: number;
  mob: number;
  layer: 0 | 1;
}
const WORM_SEGMENTS: WormSegment[] = [
  // Tail (dangling behind the shank)
  { x: -12, y: -25, r: 3, mob: 1.0, layer: 0 },
  { x: -8, y: -22, r: 3.5, mob: 0.9, layer: 0 },
  { x: -2, y: -18, r: 4, mob: 0.7, layer: 0 },
  // Wrapping the right side of the shank (in front)
  { x: 4, y: -12, r: 4.5, mob: 0.4, layer: 1 },
  { x: 6, y: -6, r: 5, mob: 0.2, layer: 1 },
  { x: 4, y: 0, r: 5.5, mob: 0.1, layer: 1 },
  // Wrapping the left side of the shank (behind)
  { x: -2, y: 5, r: 6, mob: 0.2, layer: 0 },
  { x: -6, y: 12, r: 6.5, mob: 0.4, layer: 0 },
  // Bottom loop near the bend (in front)
  { x: -2, y: 20, r: 7, mob: 0.3, layer: 1 },
  { x: 6, y: 26, r: 7, mob: 0.2, layer: 1 },
  { x: 14, y: 29, r: 7.5, mob: 0.1, layer: 1 },
  { x: 22, y: 27, r: 7, mob: 0.1, layer: 1 },
  // Impaled part pushed up the barb (in front)
  { x: 28, y: 20, r: 6.5, mob: 0.05, layer: 1 },
  { x: 30, y: 12, r: 6, mob: 0.0, layer: 1 },
  { x: 30, y: 5, r: 5.5, mob: 0.0, layer: 1 },
  // Head dangling (in front)
  { x: 32, y: -1, r: 5, mob: 0.3, layer: 1 },
  { x: 36, y: -5, r: 4, mob: 0.7, layer: 1 },
  { x: 40, y: -6, r: 3.5, mob: 1.0, layer: 1 },
];

export interface HookState {
  /** Travel *start* position in [0,1] lake coords (set by the cast-issuer). */
  x: number;
  y: number;
  /** Where the hook is travelling to. */
  targetX: number;
  targetY: number;
  /** Travel start time (ms) and duration. */
  departMs: number;
  travelMs: number;
  /** The action that produced this cast (drives bait colour + hook size). */
  action: Action;
  /** Latest catch, if any — drives the catch-animation. `atMs` is when the
   *  splash should begin (when the hook arrives at the target). */
  lastCatch: {
    caught: boolean;
    /** Overall match quality in [0,1] — drives caught-fish size (§4.2). */
    match: number;
    atMs: number;
  } | null;
}

export function makeHook(): HookState {
  return {
    x: 0.5,
    y: 0.5,
    targetX: 0.5,
    targetY: 0.5,
    departMs: 0,
    travelMs: 1,
    action: { x: 0.5, y: 0.5, r: 0.5, g: 0.5, b: 0.5, size: 0.5 },
    lastCatch: null,
  };
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private bg: HTMLCanvasElement;
  private heat: HTMLCanvasElement;
  private heatCtx: CanvasRenderingContext2D;
  private heatImageData: ImageData;
  private layout: AnchorLayout = defaultLayout();
  /** Flipped by the main loop after each bandit update; cleared once drawn. */
  beliefDirty = true;
  /** Cached agent best-guess — driven from the bandit's weights each recompute. */
  private agentGuess = { color: { r: 0.5, g: 0.5, b: 0.5 }, size: 0.5, x: 0.5, y: 0.5 };
  /** Smoothed flock centre (visual interpolation toward lake.preference). */
  private flockCx = 0.5;
  private flockCy = 0.5;

  constructor(private canvas: HTMLCanvasElement, private lake: Lake) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not supported');
    this.ctx = ctx;
    this.bg = document.createElement('canvas');
    this.heat = document.createElement('canvas');
    this.heat.width = HEATMAP_GRID;
    this.heat.height = HEATMAP_GRID;
    const heatCtx = this.heat.getContext('2d');
    if (!heatCtx) throw new Error('2D canvas not supported');
    this.heatCtx = heatCtx;
    this.heatImageData = this.heatCtx.createImageData(HEATMAP_GRID, HEATMAP_GRID);
    this.resize();
  }

  /** Swap the lake the renderer reads from (used when a new episode starts). */
  setLake(lake: Lake): void {
    this.lake = lake;
    // Reset the smoothed flock centre so the new flock doesn't glide in from
    // the previous lake's last position.
    this.flockCx = lake.preference.x;
    this.flockCy = lake.preference.y;
    this.beliefDirty = true;
  }

  /** Call on viewport resize; re-renders the static background. */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w === 0 || h === 0) return;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.renderBackground();
  }

  get widthPx(): number {
    return this.canvas.getBoundingClientRect().width;
  }
  get heightPx(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  /** Pre-render the static sky/water gradient to an offscreen buffer. */
  private renderBackground(): void {
    const w = this.canvas.getBoundingClientRect().width;
    const h = this.canvas.getBoundingClientRect().height;
    this.bg.width = Math.floor(w);
    this.bg.height = Math.floor(h);
    const ctx = this.bg.getContext('2d');
    if (!ctx) return;
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, LAKE_TOP);
    grad.addColorStop(1, LAKE_BOTTOM);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let y = 12; y < h; y += 18) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
  }

  /**
   * Recompute the heat-map from the agent's current belief (§6.1).
   *
   * For each pixel (x, y) we evaluate the bandit's predicted reward at that
   * position, plugging in the agent's current best-guess colour and size.
   * The spatial block is a 2-D grid of RBFs, so this is a forward pass
   * through a linear model at each of HEATMAP_GRID² pixels.
   *
   * The prediction is a raw linear score U^T φ (LinUCB predicts expected
   * reward directly — no sigmoid). We normalise to [0, 1] for the colormap
   * using a running min/max of the visible lake; the *relative* pattern is
   * what the viewer reads as "brighter = more confident".
   */
  recomputeHeatmap(bandit: LinUcb): void {
    const { U } = bandit;
    const layout = this.layout;
    // Read the agent's best-guess colour, size, and position straight from
    // the per-block argmaxes. These feed both the heat-map projection and
    // the crosshair.
    const r = topMarginalValue(R_BLOCK, U, layout);
    const g = topMarginalValue(G_BLOCK, U, layout);
    const b = topMarginalValue(B_BLOCK, U, layout);
    const size = topMarginalValue(SIZE_BLOCK, U, layout);
    const pos = topSpatial(U, layout);
    this.agentGuess = { color: { r, g, b }, size, x: pos.x, y: pos.y };

    // Two passes: first compute raw scores + their min/max, then colour-map.
    const scores = new Float64Array(HEATMAP_GRID * HEATMAP_GRID);
    let smin = Infinity;
    let smax = -Infinity;
    const M = layout.M;
    const phi = new Float64Array(M);
    for (let py = 0; py < HEATMAP_GRID; py++) {
      const y = py / (HEATMAP_GRID - 1);
      for (let px = 0; px < HEATMAP_GRID; px++) {
        const x = px / (HEATMAP_GRID - 1);
        featurizePixel(x, y, this.agentGuess.color, this.agentGuess.size, layout, phi);
        const s = bandit.predict(phi);
        scores[py * HEATMAP_GRID + px] = s;
        if (s < smin) smin = s;
        if (s > smax) smax = s;
      }
    }
    const range = smax - smin > 1e-9 ? smax - smin : 1;
    const data = this.heatImageData.data;
    for (let i = 0; i < scores.length; i++) {
      const p = (scores[i] - smin) / range;
      const [cr, cg, cb] = heatColor(p);
      const idx = i * 4;
      data[idx] = cr;
      data[idx + 1] = cg;
      data[idx + 2] = cb;
      data[idx + 3] = Math.floor(40 + 180 * p);
    }
    this.heatCtx.putImageData(this.heatImageData, 0, 0);
    this.beliefDirty = false;
  }

  get agentGuessRead() {
    return this.agentGuess;
  }

  /** The hook's current rendered position (for seeding a new cast's start). */
  currentHookPos(hook: HookState, t: number): { x: number; y: number } {
    const u = Math.min(1, Math.max(0, (t - hook.departMs) / hook.travelMs));
    const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    return {
      x: lerp(hook.x, hook.targetX, e),
      y: lerp(hook.y, hook.targetY, e),
    };
  }

  /** One frame. `t` is a monotonic clock in ms. */
  frame(t: number, hook: HookState, bandit: LinUcb): void {
    const w = this.widthPx;
    const h = this.heightPx;
    // Catch up on sizing if layout wasn't ready at construction.
    if (w === 0 || h === 0 || this.bg.width === 0 || this.bg.height === 0) {
      this.resize();
      if (this.bg.width === 0 || this.bg.height === 0) return;
    }
    const ctx = this.ctx;

    // Layer 1 — background.
    ctx.drawImage(this.bg, 0, 0, w, h);

    // Layer 2 — heat-map. Recompute only when the bandit has new weights.
    if (this.beliefDirty) this.recomputeHeatmap(bandit);
    ctx.globalAlpha = 0.5;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.heat, 0, 0, w, h);
    ctx.globalAlpha = 1;

    // Layer 3 — flock. Count tracks the (depletable) population.
    this.drawFlock(t);

    // Layer 4 — true fish-location marker (the legend's "location" value on
    // the lake). The agent's belief is shown by the heat-map + the worm's
    // landing spot; the crosshair was redundant with both and has been
    // removed to keep the canvas calm.
    this.drawTrueMarker();

    // Layer 5 — hook + line + worm + catch effect.
    this.drawHook(t, hook);
  }

  /**
   * Flock — silhouettes spread isotropically around the drifting centre.
   * Fish are drawn dark (deep blue/black) regardless of the hidden colour
   * preference — the preference is shown in the legend panel, not on the
   * fish themselves. Density falls off with radius (clustered centre,
   * sparse edges) mirroring the Gaussian catch profile. The flock count is
   * fixed — fish don't "disappear" as the agent learns.
   */
  private drawFlock(t: number): void {
    const w = this.widthPx;
    const h = this.heightPx;
    const ctx = this.ctx;
    // Smooth the centre visually so drift doesn't read as directed jumps.
    // The lake updates preference once per round; we interpolate toward it
    // each frame so the flock appears to glide.
    const targetX = this.lake.preference.x;
    const targetY = this.lake.preference.y;
    this.flockCx += (targetX - this.flockCx) * 0.08;
    this.flockCy += (targetY - this.flockCy) * 0.08;
    const cx = this.flockCx * w;
    const cy = this.flockCy * h;

    const tt = t * 0.001;
    // Lake size for radial placement — use the min dimension so the spread
    // looks circular rather than stretched on wide canvases.
    const lakeSize = Math.min(w, h);
    for (let i = 0; i < this.lake.flock.length; i++) {
      const f = this.lake.flock[i];
      // Position on the ring + a slow bob. Radius is a fraction of the lake.
      const r = f.radius * lakeSize;
      const wob = Math.sin(tt + f.phase) * f.amp * lakeSize;
      const x = cx + Math.cos(f.angle) * (r + wob);
      const y = cy + Math.sin(f.angle) * (r + wob);
      // Centre fish bigger, edge fish smaller.
      const s = 7 * f.sizeMul;
      this.drawFish(ctx, x, y, s, t + i * 100);
    }
  }

  /**
   * True fish-location marker — a small dotted ring at the actual flock
   * centre. Distinct from the agent's crosshair (solid ring): the viewer can
   * see at a glance how far the agent's belief is from the truth. Drawn from
   * the smoothed flock centre so it sits inside the visible fish cluster.
   */
  private drawTrueMarker(): void {
    const w = this.widthPx;
    const h = this.heightPx;
    const ctx = this.ctx;
    const px = this.flockCx * w;
    const py = this.flockCy * h;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.arc(px, py, 20, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /**
   * A cartoon fish: quadratic-curve tail, top + bottom fins behind an ellipse
   * body, a white eye with pupil, and a mouth arc. Ported from the standalone
   * fish-flock asset viewer; the body radius in that viewer's space is 16, so
   * we scale by `s / 16` to map onto this renderer's `s` (body radius).
   *
   * Fish are kept DARK (deep blue, near-black) and translucent regardless of
   * the hidden colour preference — the preference lives in the legend panel,
   * not on the fish. Painting the flock the preferred colour would contradict
   * the demo's thesis (the agent must *infer* colour from catches). The eye
   * and mouth stay opaque so the silhouette reads at small flock sizes.
   */
  private drawFish(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    s: number,
    t: number,
  ): void {
    ctx.save();
    ctx.translate(x, y);
    const wiggle = Math.sin(t * 0.005) * 0.3;
    ctx.rotate(wiggle);
    ctx.scale(s / 16, s / 16); // viewer space: body radiusX = 16

    const bodyColor = '#0a1a2a';
    const finColor = '#061320'; // slightly darker — gives fins definition behind the body
    ctx.globalAlpha = 0.78;
    ctx.lineCap = 'round';

    // 1. Tail (drawn first, behind the body).
    ctx.fillStyle = finColor;
    ctx.beginPath();
    ctx.moveTo(-10, 0);
    ctx.lineTo(-24, -12);
    ctx.quadraticCurveTo(-18, 0, -24, 12);
    ctx.fill();

    // 2. Top fin.
    ctx.beginPath();
    ctx.moveTo(-4, -10);
    ctx.quadraticCurveTo(2, -18, 8, -8);
    ctx.fill();

    // 3. Bottom fin.
    ctx.beginPath();
    ctx.moveTo(-2, 8);
    ctx.quadraticCurveTo(2, 16, 6, 8);
    ctx.fill();

    // 4. Body ellipse (over the fins, so they peek out at the edges).
    ctx.fillStyle = bodyColor;
    ctx.beginPath();
    ctx.ellipse(0, 0, 16, 11, 0, 0, Math.PI * 2);
    ctx.fill();

    // 5. Eye — opaque so it reads on the translucent body and against the lake.
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(8, -3, 3.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(9, -3, 1.5, 0, Math.PI * 2);
    ctx.fill();

    // 6. Mouth.
    ctx.strokeStyle = finColor;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(12, 3, 3, 0.2 * Math.PI, 0.8 * Math.PI);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * A single worm layer (0 = behind the hook, 1 = in front). Each segment is
   * an outlined circle; the per-segment phase wobble makes the free segments
   * (tail, head) sway while the impaled mid-body stays put. The head carries
   * an eye + glint.
   */
  private drawWormLayer(
    ctx: CanvasRenderingContext2D,
    targetLayer: 0 | 1,
    scale: number,
    color: string,
    t: number,
  ): void {
    const last = WORM_SEGMENTS.length - 1;
    for (let i = 0; i <= last; i++) {
      const seg = WORM_SEGMENTS[i];
      if (seg.layer !== targetLayer) continue;
      const wiggleX = Math.sin(t * 0.005 + i * 0.5) * 5 * seg.mob;
      const wiggleY = Math.cos(t * 0.005 + i * 0.5) * 5 * seg.mob;
      const sx = seg.x * scale + wiggleX;
      const sy = seg.y * scale + wiggleY;
      const sr = seg.r * scale;

      // Body.
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Eye on the head segment (front layer only).
      if (i === last && targetLayer === 1) {
        ctx.fillStyle = '#111';
        ctx.beginPath();
        ctx.arc(sx + 1 * scale, sy - 1 * scale, 1.2 * scale, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(sx + 1.5 * scale, sy - 1.5 * scale, 0.4 * scale, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /**
   * Hook + bait combined, drawn in three depth layers so the worm reads as
   * threaded *through* the hook rather than floating beside it:
   *
   *   1. background worm segments (behind the metal)
   *   2. the metallic hook itself (eyelet → shank → U-bend → point → barb,
   *      with a specular highlight)
   *   3. foreground worm segments (over the metal)
   *   4. the hook tip + barb redrawn on top — sells the pierce-through.
   *
   * Scale ties to the action's hook size so bigger baits get bigger hooks.
   * Ported from the standalone worm-on-hook asset viewer.
   */
  private drawWormOnHook(
    ctx: CanvasRenderingContext2D,
    px: number,
    py: number,
    a: Action,
    t: number,
  ): void {
    const scale = hookAssetScale(a.size); // hook size → overall scale
    const color = `rgb(${Math.floor(a.r * 255)},${Math.floor(a.g * 255)},${Math.floor(a.b * 255)})`;

    ctx.save();
    ctx.translate(px, py);
    ctx.scale(scale, scale);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Layer 1 — background worm segments.
    this.drawWormLayer(ctx, 0, 1, color, t);

    // Layer 2 — metallic hook body. A drop shadow gives it weight over the
    // background segments; we reset it before the (cheap) highlights.
    ctx.shadowColor = 'rgba(0,0,0,0.4)';
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    ctx.shadowBlur = 3;

    ctx.beginPath();
    ctx.moveTo(0, -36); // eyelet base
    ctx.lineTo(0, 15); // shank
    ctx.arc(15, 15, 15, Math.PI, 0, true); // U-bend
    ctx.lineTo(30, -2); // point
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#94a3b8'; // slate / silver
    ctx.stroke();

    // Barb.
    ctx.beginPath();
    ctx.moveTo(30, -2);
    ctx.lineTo(24, 6);
    ctx.lineWidth = 3;
    ctx.stroke();

    // Eyelet ring.
    ctx.beginPath();
    ctx.arc(0, -40, 4, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.stroke();

    // Specular highlight on the shank.
    ctx.shadowColor = 'transparent';
    ctx.beginPath();
    ctx.moveTo(-1, -38);
    ctx.lineTo(-1, 15);
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#f8fafc';
    ctx.stroke();

    // Layer 3 — foreground worm segments (wrap over the metal).
    this.drawWormLayer(ctx, 1, 1, color, t);

    // Layer 4 — hook tip + barb redrawn on top so the point reads as having
    // pierced completely through the bait body.
    ctx.beginPath();
    ctx.moveTo(30, 8); // start inside the worm body
    ctx.lineTo(30, -2); // out to the point
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#94a3b8';
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(30, -2);
    ctx.lineTo(24, 6);
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(29, 6);
    ctx.lineTo(29, -1);
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#f8fafc';
    ctx.stroke();

    ctx.restore();
  }

  /** Draw the hook, line, bait, and catch effect. */
  private drawHook(t: number, hook: HookState): void {
    const w = this.widthPx;
    const h = this.heightPx;
    const ctx = this.ctx;

    const u = Math.min(1, Math.max(0, (t - hook.departMs) / hook.travelMs));
    const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    const x = lerp(hook.x, hook.targetX, e);
    const y = lerp(hook.y, hook.targetY, e);
    if (u >= 1) {
      hook.x = hook.targetX;
      hook.y = hook.targetY;
    }
    const px = x * w;
    const py = y * h;

    // The hook's eyelet sits 40 local units above the cast target. We
    // translate the worm+hook asset by that offset so the *bait* lands at
    // the cast point (px, py) and the eyelet/line attach above it. Scale
    // must match drawWormOnHook so the eyelet pixel-offset tracks it.
    const hookScale = hookAssetScale(hook.action.size);
    const eyeletY = py + 40 * hookScale;

    // Line from top of canvas down to the eyelet.
    ctx.strokeStyle = HOOK_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, eyeletY);
    ctx.stroke();

    // Bait + hook — a worm threaded through a metallic hook. Colour comes
    // from the action's RGB; overall scale grows with the action's hook
    // size. The asset is offset so the bait body sits at the cast point.
    this.drawWormOnHook(ctx, px, py + 40 * hookScale, hook.action, t);

    // Catch animation: a fish on the hook + an expanding ripple. Gated to
    // [atMs, atMs + 900ms] — the splash is scheduled for when the hook lands.
    const since = hook.lastCatch ? t - hook.lastCatch.atMs : Infinity;
    if (hook.lastCatch && since >= 0 && since < 900) {
      const k = since / 900;
      if (hook.lastCatch.caught) {
        // Fish scales with overall match quality (§4.2). A centred cast with
        // matching colour/size produces a big fish; a scraping cast a small one.
        const s = Math.max(3, 5 + hook.lastCatch.match * 18);
        ctx.save();
        ctx.translate(px, py - s - 4 - k * 10);
        ctx.globalAlpha = 1 - k;
        this.drawFish(ctx, 0, 0, s, t);
        ctx.restore();
      }
      // Ripple — expanding ring on either outcome.
      ctx.strokeStyle = `rgba(255,255,255,${0.5 * (1 - k)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px, py, Math.max(1, 6 + k * 30), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}

/**
 * Overall worm+hook scale, driven by the action's hook size. The baseline
 * (size = 0) is 0.25 — the asset was ported from a viewer that drew it ~4×
 * this size, which read as far too large on the lake. Shared by
 * drawWormOnHook (which applies it) and drawHook (which needs the eyelet
 * pixel-offset to match exactly).
 */
function hookAssetScale(size: number): number {
  return (1 + size * 1.5) / 4;
}

/** Linear interpolation. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Single-hue heat-map colour for a probability p ∈ [0, 1].
 * Deep blue → cyan → near-white. Colour-blind safe (no red/green axis).
 */
function heatColor(p: number): [number, number, number] {
  const q = Math.max(0, Math.min(1, p));
  const stops: Array<[number, [number, number, number]]> = [
    [0.0, [10, 40, 90]],
    [0.5, [90, 200, 230]],
    [1.0, [235, 250, 255]],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const [p0, c0] = stops[i];
    const [p1, c1] = stops[i + 1];
    if (q >= p0 && q <= p1) {
      const u = (q - p0) / (p1 - p0);
      return [
        c0[0] + (c1[0] - c0[0]) * u,
        c0[1] + (c1[1] - c0[1]) * u,
        c0[2] + (c1[2] - c0[2]) * u,
      ];
    }
  }
  return stops[stops.length - 1][1];
}
